/* Vault — global storefront behaviour. Vanilla JS, no dependencies. */
(() => {
  'use strict';

  const TC = (window.TC = window.TC || {});
  TC.routes = TC.routes || {};
  TC.strings = TC.strings || {};

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const routeJS = (route) => `${route.replace(/\/$/, '')}.js`;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  TC.qs = qs;
  TC.qsa = qsa;
  TC.debounce = debounce;
  TC.parseHTML = parseHTML;

  /* ---------------------------------------------------------------------- */
  /* Toasts                                                                 */
  /* ---------------------------------------------------------------------- */
  TC.toast = (message, { error = false } = {}) => {
    const region = qs('[data-tc-toasts]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'tc-toast';
    el.innerHTML = error
      ? `<svg class="tc-icon" viewBox="0 0 24 24" aria-hidden="true" style="color:#ff9a9d"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.3v.2"/></svg>`
      : `<svg class="tc-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>`;
    const span = document.createElement('span');
    span.textContent = message;
    el.appendChild(span);
    region.appendChild(el);
    setTimeout(() => {
      el.classList.add('is-leaving');
      el.addEventListener('animationend', () => el.remove(), { once: true });
      setTimeout(() => el.remove(), 400);
    }, 2600);
  };

  /* ---------------------------------------------------------------------- */
  /* Scroll lock (ref-counted)                                              */
  /* ---------------------------------------------------------------------- */
  let lockCount = 0;
  TC.lockScroll = () => {
    lockCount += 1;
    if (lockCount === 1) {
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbw > 0 ? `${sbw}px` : '';
      document.body.classList.add('tc-locked');
    }
  };
  TC.unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('tc-locked');
      document.body.style.paddingRight = '';
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Drawer                                                                 */
  /* ---------------------------------------------------------------------- */
  class TcDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.tc-drawer__panel', this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-tc-close]')) {
          const link = e.target.closest('a[href]');
          this.close(!link);
        }
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      qsa('tc-drawer.is-open').forEach((d) => d !== this && d.close(false));
      this.opener = opener || document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      TC.lockScroll();
      qsa(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        const target = qs('[data-tc-autofocus]', this) || qs(FOCUSABLE, this.panel) || this.panel;
        target.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('tc:drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      TC.unlockScroll();
      qsa(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, this.panel).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  if (!customElements.get('tc-drawer')) customElements.define('tc-drawer', TcDrawer);

  // Escape closes the top-most drawer even if focus was lost (e.g. after a re-render)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = qsa('tc-drawer.is-open');
    if (!open.length) return;
    const drawer = open[open.length - 1];
    if (drawer.contains(document.activeElement)) return; // handled by the drawer itself
    drawer.close();
  });

  TC.openDrawer = (id, opener) => {
    const drawer = document.getElementById(id);
    if (drawer && drawer.open) drawer.open(opener);
    return !!drawer;
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-tc-open]');
    if (opener) {
      e.preventDefault();
      TC.openDrawer(opener.dataset.tcOpen, opener);
      return;
    }
    const cartToggle = e.target.closest('[data-tc-cart-toggle]');
    if (cartToggle && document.getElementById('tc-cart-drawer')) {
      e.preventDefault();
      TC.openDrawer('tc-cart-drawer', cartToggle);
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Header: sticky state + mega menu                                       */
  /* ---------------------------------------------------------------------- */
  class TcHeader extends HTMLElement {
    connectedCallback() {
      this.header = qs('.tc-header', this);
      this.items = qsa('[data-tc-mega]', this);
      this.setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(() => this.setHeight()).observe(this);

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.header.classList.toggle('is-scrolled', window.scrollY > 8);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      this.items.forEach((item) => this.bindItem(item));
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
    }

    setHeight() {
      document.documentElement.style.setProperty('--tc-header-h', `${this.offsetHeight}px`);
    }

    bindItem(item) {
      const btn = qs('.tc-nav__link', item);
      let timer;
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => this.open(item, true), 80);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => this.close(item), 180);
      });
      btn.addEventListener('click', () => {
        const recentlyHovered = item.dataset.hoverOpenedAt && Date.now() - Number(item.dataset.hoverOpenedAt) < 450;
        if (item.classList.contains('is-open') && !recentlyHovered) this.close(item);
        else this.open(item);
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          this.close(item);
          btn.focus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) this.close(item);
      });
    }

    open(item, viaHover = false) {
      this.items.forEach((other) => other !== item && this.close(other));
      if (viaHover && !item.classList.contains('is-open')) item.dataset.hoverOpenedAt = Date.now();
      item.classList.add('is-open');
      qs('.tc-nav__link', item).setAttribute('aria-expanded', 'true');
    }

    close(item) {
      item.classList.remove('is-open');
      qs('.tc-nav__link', item).setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }
  }
  if (!customElements.get('tc-header')) customElements.define('tc-header', TcHeader);

  // "/" focuses the visible search field
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    const tag = (document.activeElement && document.activeElement.tagName) || '';
    if (/INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement.isContentEditable) return;
    const field = qsa('.tc-searchbar input[type="search"]').find((el) => el.offsetParent !== null);
    if (field) {
      e.preventDefault();
      field.focus();
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Predictive search                                                      */
  /* ---------------------------------------------------------------------- */
  class TcSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-tc-results]', this);
      this.defaults = qs('[data-tc-default]', this);
      this.cache = new Map();
      this.selected = -1;

      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      document.addEventListener('pointerdown', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.selected = -1;
    }

    async onInput() {
      const q = this.input.value.trim();
      this.open();
      if (!q) {
        this.results.innerHTML = '';
        this.defaults.hidden = false;
        return;
      }
      this.defaults.hidden = true;
      if (this.cache.has(q)) {
        this.render(this.cache.get(q));
        return;
      }
      this.results.innerHTML = `<div class="tc-search-panel__inner tc-search-panel__loading" aria-label="${TC.strings.loading || ''}">${'<div class="tc-skeleton"></div>'.repeat(3)}</div>`;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'tc-predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.sku,vendor,tag',
      });
      try {
        const res = await fetch(`${TC.routes.predictive || '/search/suggest'}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const html = (qs('[data-tc-predictive]', doc) || doc.body).innerHTML;
        this.cache.set(q, html);
        if (this.input.value.trim() === q) this.render(html);
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.selected = -1;
      TC.fadeImages(this.results);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      const options = qsa('[role="option"]', this.results);
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.selected >= 0 && options[this.selected]) {
          e.preventDefault();
          options[this.selected].click();
        }
        return;
      }
      e.preventDefault();
      this.selected = e.key === 'ArrowDown' ? (this.selected + 1) % options.length : (this.selected - 1 + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', i === this.selected ? 'true' : 'false'));
      options[this.selected].scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', options[this.selected].id || '');
    }
  }
  if (!customElements.get('tc-search')) customElements.define('tc-search', TcSearch);

  /* ---------------------------------------------------------------------- */
  /* Cart                                                                   */
  /* ---------------------------------------------------------------------- */
  const cartRoots = () => qsa('[data-tc-cart-root]');
  const cartSectionIds = () => cartRoots().map((el) => el.dataset.sectionId).filter(Boolean);

  TC.setCartCount = (count) => {
    qsa('[data-tc-cart-count]').forEach((el) => {
      const prev = el.dataset.count;
      el.dataset.count = count;
      el.textContent = count > 0 ? count : '';
      if (String(prev) !== String(count) && !reducedMotion()) {
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      }
    });
  };

  // Describe the focused cart control so it can be re-focused after a re-render
  const focusKey = (root) => {
    const el = document.activeElement;
    if (!el || !root.contains(el)) return null;
    const line = el.closest('[data-line]');
    const attr = ['data-tc-qty', 'data-tc-remove', 'data-tc-qty-input'].find((a) => el.hasAttribute(a));
    return { line: line && line.dataset.line, attr, value: attr ? el.getAttribute(attr) : null };
  };
  const restoreFocus = (root, key) => {
    if (!key) return;
    let target = null;
    if (key.line && key.attr) {
      const line = qs(`[data-line="${key.line}"]`, root);
      const sel = key.value ? `[${key.attr}="${key.value}"]` : `[${key.attr}]`;
      target = line && qs(sel, line);
    }
    target = target || qs(FOCUSABLE, root) || root.closest('.tc-drawer__panel');
    if (target) target.focus({ preventScroll: true });
  };

  TC.renderCartSections = (sections) => {
    if (!sections) return;
    cartRoots().forEach((root) => {
      const fKey = focusKey(root);
      const html = sections[root.dataset.sectionId];
      if (!html) return;
      const doc = parseHTML(html);
      const fresh = qs('[data-tc-cart-root]', doc);
      if (!fresh) return;
      root.innerHTML = fresh.innerHTML;
      const pill = qs('[data-tc-cart-pill]', doc);
      const drawer = root.closest('tc-drawer, [data-tc-cart-page]');
      const currentPill = drawer && qs('[data-tc-cart-pill]', drawer);
      if (pill && currentPill) currentPill.textContent = pill.textContent;
      const head = qs('[data-tc-cart-heading]', doc);
      const currentHead = drawer && qs('[data-tc-cart-heading]', drawer);
      if (head && currentHead) currentHead.innerHTML = head.innerHTML;
      if (fresh.dataset.itemCount !== undefined) TC.setCartCount(Number(fresh.dataset.itemCount));
      TC.fadeImages(root);
      TC.wishlist.sync(root);
      restoreFocus(root, fKey);
    });
  };

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.description || data.message || TC.strings.error), { data });
    return data;
  };

  TC.cart = {
    async add(formData) {
      const ids = cartSectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(routeJS(TC.routes.cartAdd || '/cart/add'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || TC.strings.error);
      if (data.sections) TC.renderCartSections(data.sections);
      else TC.cart.refreshCount();
      document.dispatchEvent(new CustomEvent('tc:cart:added', { detail: data }));
      return data;
    },

    async change(line, quantity) {
      const ids = cartSectionIds();
      const data = await postJSON(routeJS(TC.routes.cartChange || '/cart/change'), {
        line,
        quantity,
        sections: ids,
        sections_url: window.location.pathname,
      });
      if (data.sections) TC.renderCartSections(data.sections);
      TC.setCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('tc:cart:changed', { detail: data }));
      return data;
    },

    async refreshCount() {
      const res = await fetch(routeJS(TC.routes.cart || '/cart'), { headers: { Accept: 'application/json' } });
      const data = await res.json();
      TC.setCartCount(data.item_count);
    },
  };

  const setBtnState = (btn, state) => {
    if (!btn) return;
    btn.classList.remove('is-loading', 'is-done');
    if (state) btn.classList.add(state);
    btn.setAttribute('aria-busy', state === 'is-loading' ? 'true' : 'false');
  };

  // Add-to-cart: quick add forms + product form
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-tc-atc-form], form[data-tc-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter && e.submitter.type === 'submit' ? e.submitter : qs('[type="submit"]', form);
    if (btn && (btn.disabled || btn.classList.contains('is-loading'))) return;
    const errorEl = qs('[data-tc-form-error]', form.closest('[data-tc-product]') || form);
    if (errorEl) errorEl.hidden = true;
    setBtnState(btn, 'is-loading');
    try {
      await TC.cart.add(new FormData(form));
      setBtnState(btn, 'is-done');
      setTimeout(() => setBtnState(btn, null), 1600);
      const drawer = document.getElementById('tc-cart-drawer');
      if (drawer && form.dataset.tcOpenCart !== 'false') {
        drawer.open(btn);
      } else {
        TC.toast(TC.strings.added || 'Added to cart');
      }
    } catch (err) {
      setBtnState(btn, null);
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } else {
        TC.toast(err.message || TC.strings.error, { error: true });
      }
    }
  });

  // Quantity steppers (cart lines + product form)
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-tc-qty]');
    if (step) {
      const wrap = step.closest('.tc-qty');
      const input = wrap && qs('input', wrap);
      if (!input) return;
      const min = input.closest('[data-tc-cart-root]') ? 0 : Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(min, Number(input.value || 0) + (step.dataset.tcQty === 'plus' ? 1 : -1)));
      if (next === Number(input.value)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-tc-remove]');
    if (remove) {
      const line = remove.closest('[data-line]');
      if (line) updateLine(line, 0);
    }
  });

  const updateLine = async (lineEl, qty) => {
    const errorEl = qs('[data-tc-line-error]', lineEl);
    lineEl.classList.add('is-updating');
    try {
      await TC.cart.change(Number(lineEl.dataset.line), qty);
    } catch (err) {
      lineEl.classList.remove('is-updating');
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    }
  };

  document.addEventListener(
    'change',
    debounce((e) => {
      const input = e.target.closest('[data-tc-qty-input]');
      if (!input) return;
      const line = input.closest('[data-line]');
      if (line && input.closest('[data-tc-cart-root]')) updateLine(line, Math.max(0, Number(input.value || 0)));
    }, 300)
  );

  // Order note (cart page)
  document.addEventListener(
    'input',
    debounce((e) => {
      const note = e.target.closest('[data-tc-cart-note]');
      if (!note) return;
      postJSON(routeJS(TC.routes.cartUpdate || '/cart/update'), { note: note.value }).catch(() => {});
    }, 500)
  );

  /* ---------------------------------------------------------------------- */
  /* Wishlist (device storage)                                              */
  /* ---------------------------------------------------------------------- */
  const WKEY = 'tc:wishlist';
  TC.wishlist = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(WKEY)) || [];
      } catch (e) {
        return [];
      }
    },
    set(list) {
      try {
        localStorage.setItem(WKEY, JSON.stringify(list.slice(0, 100)));
      } catch (e) {
        /* storage unavailable */
      }
      document.dispatchEvent(new CustomEvent('tc:wishlist', { detail: list }));
    },
    has(handle) {
      return this.get().includes(handle);
    },
    toggle(handle) {
      const list = this.get();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.unshift(handle);
      this.set(list);
      return i === -1;
    },
    sync(root = document) {
      const list = this.get();
      qsa('[data-tc-wish]', root).forEach((btn) => {
        const on = list.includes(btn.dataset.tcWish);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
        if (label) btn.setAttribute('aria-label', label);
      });
      qsa('[data-tc-wish-count]').forEach((el) => {
        el.textContent = list.length > 0 ? list.length : '';
        el.dataset.count = list.length;
      });
    },
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tc-wish]');
    if (!btn) return;
    e.preventDefault();
    const added = TC.wishlist.toggle(btn.dataset.tcWish);
    TC.wishlist.sync();
    if (added && !reducedMotion()) {
      btn.classList.remove('is-popping');
      void btn.offsetWidth;
      btn.classList.add('is-popping');
      setTimeout(() => btn.classList.remove('is-popping'), 600);
    }
    TC.toast(added ? TC.strings.wishAdded : TC.strings.wishRemoved);
  });
  window.addEventListener('storage', (e) => e.key === WKEY && TC.wishlist.sync());

  /* ---------------------------------------------------------------------- */
  /* Product cards by handle (wishlist page, recently viewed)               */
  /* ---------------------------------------------------------------------- */
  TC.fetchCards = async (handles) => {
    const base = TC.routes.products || '/products/';
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`${base}${encodeURIComponent(h)}?view=tc-card`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );
    return results.map((html) => html.trim()).filter((html) => html.startsWith('<'));
  };

  TC.recent = {
    key: 'tc:recent',
    get() {
      try {
        return JSON.parse(localStorage.getItem(this.key)) || [];
      } catch (e) {
        return [];
      }
    },
    push(handle) {
      const list = this.get().filter((h) => h !== handle);
      list.unshift(handle);
      try {
        localStorage.setItem(this.key, JSON.stringify(list.slice(0, 16)));
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  class TcCardList extends HTMLElement {
    async connectedCallback() {
      const source = this.dataset.source;
      const exclude = this.dataset.exclude;
      const limit = Number(this.dataset.limit || 8);
      const grid = qs('[data-tc-list-grid]', this);
      const empty = qs('[data-tc-list-empty]', this);
      const handles = (source === 'wishlist' ? TC.wishlist.get() : TC.recent.get()).filter((h) => h !== exclude).slice(0, limit);
      if (!handles.length) {
        if (empty) empty.hidden = false;
        if (this.dataset.hideEmpty !== undefined) this.closest('[data-tc-hide-when-empty]')?.setAttribute('hidden', '');
        if (grid) grid.innerHTML = '';
        return;
      }
      if (grid) grid.innerHTML = '<div class="tc-skeleton" style="aspect-ratio:4/5.9"></div>'.repeat(Math.min(handles.length, 4));
      const cards = await TC.fetchCards(handles);
      if (!cards.length) {
        if (empty) empty.hidden = false;
        if (grid) grid.innerHTML = '';
        return;
      }
      this.closest('[data-tc-hide-when-empty]')?.removeAttribute('hidden');
      grid.innerHTML = cards.map((c) => `<li>${c}</li>`).join('');
      TC.init(grid);
      if (source === 'wishlist') {
        this.onWish = () => {
          const current = TC.wishlist.get();
          qsa('[data-product-handle]', grid).forEach((card) => {
            if (!current.includes(card.dataset.productHandle)) {
              card.style.transition = 'opacity .25s, transform .25s';
              card.style.opacity = '0';
              card.style.transform = 'scale(.96)';
              setTimeout(() => {
                card.remove();
                if (!qs('[data-product-handle]', grid) && empty) empty.hidden = false;
              }, 260);
            }
          });
        };
        document.addEventListener('tc:wishlist', this.onWish);
      }
    }
    disconnectedCallback() {
      if (this.onWish) document.removeEventListener('tc:wishlist', this.onWish);
    }
  }
  if (!customElements.get('tc-card-list')) customElements.define('tc-card-list', TcCardList);

  /* Product recommendations (lazy, Section Rendering API) */
  class TcRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = qs('tc-recommendations', doc);
          if (fresh && qs('[data-product-handle]', fresh)) {
            this.innerHTML = fresh.innerHTML;
            TC.init(this);
          } else {
            this.closest('[data-tc-hide-when-empty]')?.setAttribute('hidden', '');
          }
        } catch (e) {
          this.closest('[data-tc-hide-when-empty]')?.setAttribute('hidden', '');
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }
  }
  if (!customElements.get('tc-recommendations')) customElements.define('tc-recommendations', TcRecommendations);

  /* ---------------------------------------------------------------------- */
  /* Carousel                                                               */
  /* ---------------------------------------------------------------------- */
  class TcCarousel extends HTMLElement {
    connectedCallback() {
      this.track = qs('.tc-carousel__track', this);
      if (!this.track) return;
      this.prev = qs('[data-tc-prev]', this.closest('section') || this);
      this.next = qs('[data-tc-next]', this.closest('section') || this);
      this.bar = qs('.tc-carousel__progress span', this);
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }
    go(dir) {
      this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = Math.max(1, scrollWidth - clientWidth);
      const visible = Math.min(1, clientWidth / scrollWidth);
      if (this.bar) {
        this.bar.style.setProperty('--progress', `${visible * 100}%`);
        this.bar.style.setProperty('--offset', `${(scrollLeft / max) * (1 - visible) * 100}%`);
      }
      if (this.prev) this.prev.disabled = scrollLeft <= 2;
      if (this.next) this.next.disabled = scrollLeft >= max - 2;
      this.classList.toggle('is-static', scrollWidth <= clientWidth + 2);
    }
  }
  if (!customElements.get('tc-carousel')) customElements.define('tc-carousel', TcCarousel);

  /* ---------------------------------------------------------------------- */
  /* Reveal on scroll, image fade-in, counters, tilt                        */
  /* ---------------------------------------------------------------------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
        )
      : null;

  TC.reveal = (root = document) => {
    qsa('[data-tc-reveal]:not(.is-in)', root).forEach((el) => {
      if (!revealObserver || reducedMotion()) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  };

  TC.fadeImages = (root = document) => {
    qsa('img.tc-fade-img:not(.is-loaded)', root).forEach((img) => {
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
    });
  };
  document.addEventListener(
    'load',
    (e) => {
      if (e.target.tagName === 'IMG' && e.target.classList.contains('tc-fade-img')) e.target.classList.add('is-loaded');
    },
    true
  );
  document.addEventListener(
    'error',
    (e) => {
      if (e.target.tagName === 'IMG' && e.target.classList.contains('tc-fade-img')) e.target.classList.add('is-loaded');
    },
    true
  );

  const counterObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              counterObserver.unobserve(entry.target);
              runCounter(entry.target);
            });
          },
          { threshold: 0.4 }
        )
      : null;

  const runCounter = (el) => {
    const target = Number(el.dataset.tcCountTo || 0);
    const format = (n) => Math.round(n).toLocaleString(document.documentElement.lang || 'en-US');
    if (reducedMotion() || target === 0) {
      el.textContent = format(target);
      return;
    }
    const duration = 1500;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);
      el.textContent = format(target * eased);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  TC.counters = (root = document) => {
    qsa('[data-tc-count-to]', root).forEach((el) => {
      if (el.dataset.tcCounted) return;
      el.dataset.tcCounted = '1';
      if (reducedMotion()) return; // keep the server-rendered value
      if (!counterObserver) runCounter(el);
      else {
        el.textContent = '0';
        counterObserver.observe(el);
      }
    });
  };

  TC.tilt = (root = document) => {
    if (!finePointer() || reducedMotion()) return;
    qsa('[data-tc-tilt]', root).forEach((el) => {
      if (el.dataset.tcTiltBound) return;
      el.dataset.tcTiltBound = '1';
      const strength = Number(el.dataset.tcTilt || 8);
      let frame;
      el.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          el.style.setProperty('--ry', `${(x - 0.5) * strength}deg`);
          el.style.setProperty('--rx', `${(0.5 - y) * strength}deg`);
          el.style.setProperty('--mx', `${x * 100}%`);
          el.style.setProperty('--my', `${y * 100}%`);
          el.classList.add('is-tilting');
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        el.classList.remove('is-tilting');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  };

  /* Footer accordions: collapsed on mobile, always open on desktop */
  const footerAccordions = () => {
    const mq = window.matchMedia('(max-width: 749px)');
    const apply = () =>
      qsa('[data-tc-footer-acc]').forEach((d) => {
        d.open = !mq.matches;
      });
    apply();
    mq.addEventListener?.('change', apply);
    document.addEventListener('click', (e) => {
      const summary = e.target.closest('[data-tc-footer-acc] > summary');
      if (summary && !mq.matches) e.preventDefault();
    });
  };

  /* Copy-link share buttons */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-tc-share]');
    if (!btn) return;
    const url = btn.dataset.tcShare || window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch (err) {
        /* dismissed */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      TC.toast(TC.strings.copied);
    } catch (err) {
      /* clipboard unavailable */
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Init                                                                   */
  /* ---------------------------------------------------------------------- */
  TC.init = (root = document) => {
    TC.reveal(root);
    TC.fadeImages(root);
    TC.counters(root);
    TC.tilt(root);
    TC.wishlist.sync(root);
  };

  const boot = () => {
    TC.init();
    footerAccordions();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => TC.init(e.target));
})();
